/**
 * External Link Redirect Handler
 * 
 * This script intercepts clicks on external links and redirects them through
 * a dedicated redirect page that displays a countdown before forwarding to
 * the original destination.
 */

class ExternalLinkRedirect {
  constructor() {
    this.currentDomain = window.location.hostname;
    this.excludeDomains = ['rescuespa.myshopify.com', 'rescuespa-live.myshopify.com', 'shoprescuespa.com', 'rescuespa.com', 'beautyaficionado.com']; // Add domains to exclude from redirection if needed
    this.redirectPagePath = '/pages/redirect'; // Path to the redirect page
    
    this.init();
  }
  
  /**
   * Initialize the handler
   */
  init() {
    // Prevent double-initialization (e.g., script included twice or hot reload)
    if (window.__externalLinkRedirectInitialized) return;
    window.__externalLinkRedirectInitialized = true;

    // First add screen reader content to all external links
    this.addScreenReaderTextToExternalLinks();
    
    // Then add the click handler
    document.addEventListener('click', this.handleLinkClick.bind(this));
  }
  
  /**
   * Add screen reader text to all external links
   */
  addScreenReaderTextToExternalLinks() {
    // Get all links on the page
    const links = document.querySelectorAll('a[href]');
    
    links.forEach(link => {
      const href = link.getAttribute('href');
      
      // Skip if no href, already processed, or already has screen reader text
      if (!href || link.dataset.srEnhanced === '1') return;
      
      // Skip special links
      if (href.startsWith('mailto:') || href.startsWith('tel:') || 
          href.startsWith('sms:') || href.startsWith('#')) {
        return;
      }
      
      // Check if it's an external link
      if (this.isExternalLink(href)) {
        // Ensure only one sr-only element indicating new tab
        if (!link.querySelector('.sr-only')) {
          const srOnly = document.createElement('div');
          srOnly.className = 'sr-only';
          srOnly.textContent = '(Opens in a new tab)';
          link.appendChild(srOnly);
        }
        
        // Normalize aria-label to ensure exactly one '(Opens in a new tab)'
        const existingLabelOrText = (link.getAttribute('aria-label') || link.textContent || '').trim();
        const cleanedBase = existingLabelOrText.replace(/\s*\(Opens in a new tab\)\s*/gi, '').trim();
        link.setAttribute('aria-label', `${cleanedBase} (Opens in a new tab)`);
        
        // Mark as enhanced to prevent duplicate work
        link.dataset.srEnhanced = '1';
      }
    });
  }
  
  /**
   * Check if URL is external
   * @param {string} url - URL to check
   * @returns {boolean} True if URL is external
   */
  isExternalLink(url) {
    try {
      // If URL doesn't have protocol, it's relative and therefore internal
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return false;
      }
      
      const urlObj = new URL(url);
      
      // Check if link points to the same domain as the current page
      return urlObj.hostname !== this.currentDomain;
    } catch (e) {
      // If URL is invalid, consider it internal
      return false;
    }
  }

  /**
   * Check if URL is in the excluded domains list
   * @param {string} url - URL to check
   * @returns {boolean} True if URL is in the excluded list
   */
  isExcludedDomain(url) {
    try {
      const urlObj = new URL(url);
      return this.excludeDomains.some(domain => urlObj.hostname.includes(domain));
    } catch (e) {
      // If URL is invalid, it's not in the exclude list
      return false;
    }
  }
  
  /**
   * Handle link clicks
   * @param {Event} event - Click event
   */
  handleLinkClick(event) {
    const target = event.target.closest('a');
    
    if (!target) return;
    
    const href = target.getAttribute('href');
    
    if (!href) return;
    
    // Skip links with specific classes if needed
    if (target.classList.contains('no-redirect')) return;
    
    // Skip mailto, tel, and similar links
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('sms:') || href.startsWith('#')) {
      return;
    }
    
    // Skip links that explicitly request _self target
    if (target.getAttribute('target') === '_self') {
      return;
    }
    
    // Check if it's an external link
    if (this.isExternalLink(href)) {
      event.preventDefault();
      
      // If the domain is excluded, open directly. Otherwise, use redirect page.
      if (this.isExcludedDomain(href)) {
        window.open(href, '_blank');
      } else {
        // Encode the destination URL
        const encodedDestination = encodeURIComponent(href);
        
        // Build the redirect URL
        const redirectUrl = `${this.redirectPagePath}?destination=${encodedDestination}`;
        
        // Open in new tab
        window.open(redirectUrl, '_blank');
      }
    }
  }
}

// Initialize once DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  new ExternalLinkRedirect();
});
